import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, NumberInput, Stack, Table, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEGAL_LIMITS,
  MEASUREMENT_SPECS,
  hasCapability,
  legalLimitFor,
  type MeasurementKind,
} from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';
import { PageHeader, TableSkeleton } from '../components/Page.js';

interface ThresholdRow {
  kind: MeasurementKind;
  appliesTo: string;
  warn: number;
  critical: number;
}

/** Читаемое название позиции: роль оси или тип тормозной камеры. */
const APPLIES_LABEL: Record<string, string> = {
  steer: 'Steer axle',
  drive: 'Drive axle',
  trailer: 'Trailer axle',
  lift: 'Lift axle',
  type_16: 'Chamber type 16',
  type_20: 'Chamber type 20',
  type_24: 'Chamber type 24',
  type_24_long: 'Chamber type 24 long',
  type_30: 'Chamber type 30',
  type_30_long: 'Chamber type 30 long',
  type_36: 'Chamber type 36',
};

/**
 * Пороги износа.
 *
 * Два правила проверяются здесь же, до отправки, потому что сервер отклонит их
 * всё равно, а объяснять отказ формой поздно:
 *
 *  1. Порог флота не может быть мягче закона. Строже — можно и нужно: смысл
 *     продукта в том, чтобы поймать износ в цеху, а не на весовой.
 *  2. Предупреждение обязано срабатывать раньше критического, иначе оно
 *     бесполезно.
 *
 * Направление сравнения зависит от величины: у протектора меньше — хуже,
 * у хода штока больше — хуже.
 */
export function Thresholds(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'threshold:write');
  const queryClient = useQueryClient();

  const [rows, setRows] = useState<ThresholdRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['thresholds'],
    queryFn: () => api<{ thresholds: ThresholdRow[] }>('/thresholds'),
  });

  useEffect(() => {
    if (data === undefined) return;
    // Показываем все законодательно значимые позиции, а не только настроенные:
    // ненастроенный порог означает «действует законный предел», и это надо видеть.
    const existing = new Map(data.thresholds.map((t) => [`${t.kind}:${t.appliesTo}`, t]));
    setRows(
      LEGAL_LIMITS.map((legal) => {
        const found = existing.get(`${legal.kind}:${legal.appliesTo}`);
        if (found !== undefined) return found;

        // Значения по умолчанию обязаны быть валидными.
        // Подставлять предупреждение равным критическому значило бы встречать
        // человека стеной ошибок, которых он не создавал: правило требует,
        // чтобы предупреждение срабатывало раньше. Запас — два шага ввода
        // в сторону «хуже», то есть заранее, а не в момент нарушения.
        const spec = MEASUREMENT_SPECS[legal.kind];
        const margin = spec.step * 2;
        return {
          kind: legal.kind,
          appliesTo: legal.appliesTo,
          critical: legal.limit,
          warn: spec.worse === 'lower' ? legal.limit + margin : legal.limit - margin,
        };
      }),
    );
  }, [data]);

  /** Ошибки по строке: нужны и сводкой сверху, и подсветкой поля. */
  const rowProblem = (row: ThresholdRow): string | null => {
    const legal = legalLimitFor(row.kind, row.appliesTo);
    const spec = MEASUREMENT_SPECS[row.kind];
    const lowerIsWorse = spec.worse === 'lower';
    if (legal !== undefined) {
      const softer = lowerIsWorse ? row.critical < legal.limit : row.critical > legal.limit;
      if (softer) {
        return `more lenient than ${legal.citation} (${String(legal.limit)}${spec.unit})`;
      }
    }
    const warnTooLate = lowerIsWorse ? row.warn <= row.critical : row.warn >= row.critical;
    if (warnTooLate) return 'the warning must trigger before the critical threshold';
    return null;
  };

  const problems = rows.flatMap((row) => {
    const legal = legalLimitFor(row.kind, row.appliesTo);
    const spec = MEASUREMENT_SPECS[row.kind];
    const lowerIsWorse = spec.worse === 'lower';
    const out: string[] = [];

    if (legal !== undefined) {
      const softer = lowerIsWorse ? row.critical < legal.limit : row.critical > legal.limit;
      if (softer) {
        out.push(
          `${APPLIES_LABEL[row.appliesTo] ?? row.appliesTo}: critical threshold is more lenient than ${legal.citation} (${String(legal.limit)}${spec.unit})`,
        );
      }
    }
    const warnTooLate = lowerIsWorse ? row.warn <= row.critical : row.warn >= row.critical;
    if (warnTooLate) {
      out.push(
        `${APPLIES_LABEL[row.appliesTo] ?? row.appliesTo}: the warning must trigger before the critical threshold`,
      );
    }
    return out;
  });

  const save = useMutation({
    mutationFn: () => api('/thresholds', { method: 'PUT', body: { thresholds: rows } }),
    onSuccess: async () => {
      setSaved(true);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['thresholds'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not save thresholds');
    },
  });

  const patch = (index: number, field: 'warn' | 'critical', value: number): void => {
    setSaved(false);
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  return (
    <Stack gap={0}>
      <PageHeader
        title="Thresholds"
        hint="Fleet limits sit on top of the federal ones. You can be stricter than the law, never more lenient."
        action={
          canWrite ? (
            <Button
              loading={save.isPending}
              disabled={problems.length > 0}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          ) : undefined
        }
      />

      {problems.length > 0 && (
        <Alert color="red" mb="md" title="Fix before saving">
          <Stack gap={2}>
            {problems.map((p) => (
              <Text key={p} fz="sm">
                {p}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {saved && problems.length === 0 && (
        <Alert color="blue" mb="md">
          Thresholds saved. They apply to inspections submitted from now on; records already filed
          keep the limits that were in force when they were made.
        </Alert>
      )}

      {error !== null && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}

      {isLoading && <TableSkeleton rows={6} cols={5} />}

      {!isLoading && (
        <Table.ScrollContainer minWidth={760}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="24%">Measurement</Table.Th>
                <Table.Th w="22%">Position</Table.Th>
                <Table.Th w="16%">Legal limit</Table.Th>
                <Table.Th w="16%">Warn at</Table.Th>
                <Table.Th w="16%">Critical at</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, index) => {
                const spec = MEASUREMENT_SPECS[row.kind];
                const legal = legalLimitFor(row.kind, row.appliesTo);
                return (
                  <Table.Tr key={`${row.kind}:${row.appliesTo}`}>
                    <Table.Td>
                      <Text fz="sm" fw={560}>
                        {spec.label}
                      </Text>
                      <Text className="pf-eyebrow" mt={2}>
                        {spec.worse === 'lower' ? 'lower is worse' : 'higher is worse'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="gray.7">
                        {APPLIES_LABEL[row.appliesTo] ?? row.appliesTo}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {legal === undefined ? (
                        <Text fz="sm" c="dimmed">
                          —
                        </Text>
                      ) : (
                        <Group gap={6} wrap="nowrap">
                          <Text className="pf-num" fz="sm">
                            {legal.limit}
                            {spec.unit}
                          </Text>
                          <Badge variant="light" color="gray" size="xs">
                            {legal.citation.replace('49 CFR ', '')}
                          </Badge>
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        disabled={!canWrite}
                        error={rowProblem(row) !== null}
                        value={row.warn}
                        step={spec.step}
                        decimalScale={spec.decimals}
                        onChange={(v) => patch(index, 'warn', Number(v))}
                      />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        disabled={!canWrite}
                        error={rowProblem(row) !== null}
                        value={row.critical}
                        step={spec.step}
                        decimalScale={spec.decimals}
                        onChange={(v) => patch(index, 'critical', Number(v))}
                      />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
